// ── CAPR S2 — INITIATION (FR-N1), one control ──────────────────────────────────────────────────
//
// ⚠ "START ASSESSMENT" IS THE EXISTING ASSIGN, WITH WHAT THE ASSESSMENT IS FOR. Same endpoint, same
//   one-open-assignment rule (V1051) and the same eligibility; it adds why the assessment was
//   started (one of the six), an editable due date and an optional note.
//
// ⚠ THE ASSESSOR DEFAULTS TO THE CONTROL'S RESPONSIBLE PERSON — only when that person is eligible to
//   be assigned work. Defaulting an ineligible person would offer a choice the server refuses. Bulk
//   initiation (CAPR S5, BulkReviewBar) uses this file's own due-by calculation, not a copy.

import { useEffect, useState } from 'react';
import {
  CTS_REASONS_STARTED, CTS_REASON_STARTED_LABEL, ctsLaneEligible, type CtsReasonStarted,
} from '@grc/shared';
import { Button } from '@/components/ui/button';
import { PeoplePicker } from '@/components/compliance/PeopleLanes';
import type { ControlHub } from '@/api/types/cts-workspace.types';
import type { TeamMember } from '@/api/types/settings.types';
import { START_ACTION } from './assessment-copy';
import { formatCalendarDate } from '@/lib/calendar-date';

const LABEL = 'text-[11.5px] text-text-secondary';
const FIELD = 'rounded-[7px] border border-border-subtle bg-bg-surface px-[9px] py-[6px] text-[12.5px] text-text-primary';

/** The Responsible person, if they may be given assessment work — otherwise no default. */
export function defaultAssessor(hub: ControlHub, team: TeamMember[] | undefined): string {
  const id = hub.control.responsible_user_id;
  if (!id) return '';
  const m = (team ?? []).find((t) => t.id === id);
  return m && ctsLaneEligible('assignee', m.roles) ? id : '';
}

/**
 * ⚠ THE DUE-BY DEFAULT, AND IT IS ALWAYS EDITABLE.
 *   • RE-ASSESSMENT on a periodic cadence (CAPR S3, ruling 5): the control's NEXT REVIEW DATE — the S3
 *     resolver's own tenant-local day, used AS SENT — when that day is today or later. No date is computed
 *     here, so the default can never disagree with the "Next review" the Hub and the list show. (The
 *     resolver only sends a date when an approved decision exists and the cadence is periodic.)
 *     ⚠ "Today or later" is the RESOLVER'S comparison, not a new one: `overdue` means exactly "the next
 *     review date is before today, tenant-local", so a control that is not overdue has a next review date
 *     of today or later — and a control due TODAY reads due_soon and keeps today as its default.
 *   • FIRST ASSESSMENT (AO-05), an OVERDUE re-assessment (its next review date has passed — a past date is
 *     not a useful due-by), or a re-assessment with no scheduled review (as needed / event-driven):
 *     30 days after initiation — the S2 behaviour, unchanged.
 * Returned as a calendar day (YYYY-MM-DD), the value a date input holds.
 */
export const FIRST_ASSESSMENT_DUE_DAYS = 30;
export function defaultDueDate(hub: ControlHub, now: Date = new Date()): string {
  return defaultDueDateFor(hub.review, now);
}

/**
 * ⚠ CAPR S5 — THE SAME CALCULATION, FROM THE TWO REVIEW FACTS IT READS, so the bulk start (which has the
 * list's rows, not a Hub) calls exactly this rather than a copy. The Hub's `review` and a list row both
 * carry `next_review_date` and `review_label` from the one resolver.
 * ⓘ The +30-day path still counts from the browser's day — the tracked B-CAPR-START-FORM-TENANT-LOCAL-TODAY.
 */
export function defaultDueDateFor(
  review: { next_review_date: string | null; review_label: string }, now: Date = new Date(),
): string {
  if (review.next_review_date !== null && review.review_label !== 'overdue') return review.next_review_date;
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + FIRST_ASSESSMENT_DUE_DAYS);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A due-by calendar day as the instant the server stores: the END of that day ("due by"). */
export function dueAtFromDay(day: string): string | null {
  return day ? new Date(`${day}T23:59:59`).toISOString() : null;
}

/** ⚠ CAPR S5 — the single start's due-by for one control, end to end: its default day, as the stored instant. */
export function dueAtForReview(
  review: { next_review_date: string | null; review_label: string }, now: Date = new Date(),
): string | null {
  return dueAtFromDay(defaultDueDateFor(review, now));
}

export interface StartAssessmentValues {
  assigneeUserId: string;
  reasonStarted: CtsReasonStarted;
  dueAt: string | null;
  initiationNote: string | null;
}

export function StartAssessmentForm({ hub, team, busy, error, onStart, onCancel }: {
  hub: ControlHub;
  team: TeamMember[] | undefined;
  busy: boolean;
  error: string | null;
  onStart: (v: StartAssessmentValues) => void;
  onCancel: () => void;
}) {
  const [assignee, setAssignee] = useState(() => defaultAssessor(hub, team));
  const [touched, setTouched] = useState(false);
  // ⓘ The team list arrives asynchronously; apply the default when it does, unless a choice was made.
  useEffect(() => { if (!touched && assignee === '') setAssignee(defaultAssessor(hub, team)); }, [team, hub, touched, assignee]);
  const [reason, setReason] = useState<CtsReasonStarted | ''>('');
  const [due, setDue] = useState(() => defaultDueDate(hub));
  const [note, setNote] = useState('');
  const ready = assignee !== '' && reason !== '';

  return (
    <div className="flex flex-col gap-[8px]" data-testid="start-assessment-form">
      <label className={LABEL} htmlFor="wf-assignee">Assessor</label>
      <PeoplePicker id="wf-assignee" lane="assignee" team={team} value={assignee} onChange={(v) => { setTouched(true); setAssignee(v); }} />
      {hub.control.responsible_user_id && assignee === hub.control.responsible_user_id && (
        <span className="text-[11px] text-text-faint">The control’s Responsible person, by default.</span>
      )}

      <label className={LABEL} htmlFor="wf-reason-started">Why is this assessment being started?</label>
      <select id="wf-reason-started" value={reason} onChange={(e) => setReason(e.target.value as CtsReasonStarted)} className={FIELD}>
        <option value="">Choose a reason…</option>
        {CTS_REASONS_STARTED.map((r) => <option key={r} value={r}>{CTS_REASON_STARTED_LABEL[r]}</option>)}
      </select>

      <label className={LABEL} htmlFor="wf-due">Due by</label>
      <input id="wf-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} className={FIELD} />
      <span className="text-[11px] text-text-faint" data-testid="due-default-note">
        {hub.review.next_review_date !== null && hub.review.review_label !== 'overdue'
          ? `Re-assessment — due on the next review date (${formatCalendarDate(hub.review.next_review_date)}) by default; change it if needed.`
          : hub.review.review_label === 'overdue'
            ? `Overdue re-assessment — due ${FIRST_ASSESSMENT_DUE_DAYS} days from today by default; change it if needed.`
          : hub.review.last_approved_date !== null
            ? `No scheduled review — due ${FIRST_ASSESSMENT_DUE_DAYS} days from today by default; change it if needed.`
            : `First assessment — due ${FIRST_ASSESSMENT_DUE_DAYS} days from today by default; change it if needed.`}
      </span>

      <label className={LABEL} htmlFor="wf-note">Note for the assessor (optional)</label>
      <textarea id="wf-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={FIELD} />

      {/* ⚠ The server's own sentence — including the one-open-assignment refusal, which says what to do. */}
      {error && <p className="text-[12px] text-cert-warn-fg" role="alert">{error}</p>}

      <div className="flex gap-[8px]">
        <Button size="sm" disabled={!ready || busy} data-testid="start-assessment-confirm"
          onClick={() => reason && onStart({
            assigneeUserId: assignee,
            reasonStarted: reason,
            // a date input yields a calendar day; the end of that day is "due by"
            dueAt: dueAtFromDay(due),
            initiationNote: note.trim() === '' ? null : note.trim(),
          })}>
          {START_ACTION}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
